import React from 'react';
import { useTheme } from '../../theme/ThemeProvider';

const AnimatedBackground = ({ variant = 'geometric', intensity = 'medium' }) => {
  const { mode } = useTheme();

  // Configuraciones de animación basadas en intensidad
  const intensityConfig = {
    subtle: { 
      speed: 20, 
      opacity: mode === 'dark' ? 0.15 : 0.25, 
      count: 8 
    },
    medium: { 
      speed: 15, 
      opacity: mode === 'dark' ? 0.25 : 0.35, 
      count: 12 
    },
    strong: { 
      speed: 10, 
      opacity: mode === 'dark' ? 0.35 : 0.45, 
      count: 16 
    }
  };

  const config = intensityConfig[intensity];

  const generateGeometricShapes = () => {
    const shapes = [];
    for (let i = 0; i < config.count; i++) {
      const shapeType = ['cube', 'pyramid', 'octahedron', 'prism', 'diamond', 'sphere'][Math.floor(Math.random() * 6)];
      const size = Math.random() * 80 + 30;
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      const delay = Math.random() * 25;
      const direction = Math.random() > 0.5 ? 1 : -1;
      const rotationSpeed = Math.random() * 360 + 180;

      shapes.push({
        id: i,
        type: shapeType,
        size,
        x,
        y,
        delay,
        direction,
        rotationSpeed
      });
    }
    return shapes;
  };

  const shapes = generateGeometricShapes();

  const getShapeElement = (shape) => {
    const baseStyle = {
      position: 'absolute',
      left: `${shape.x}%`,
      top: `${shape.y}%`,
      width: `${shape.size}px`,
      height: `${shape.size}px`,
      opacity: config.opacity,
      animation: `float3D${shape.direction} ${config.speed + shape.delay}s ease-in-out infinite, rotate3D ${shape.rotationSpeed}s linear infinite`,
      animationDelay: `${shape.delay}s`,
      transformStyle: 'preserve-3d',
      perspective: '1000px',
    };

    // Colores optimizados para ambos modos
    const primaryColor = mode === 'dark' 
      ? 'rgba(41, 181, 232, 0.9)' 
      : 'rgba(41, 181, 232, 0.85)';
    
    const secondaryColor = mode === 'dark' 
      ? 'rgba(86, 196, 237, 0.7)' 
      : 'rgba(27, 150, 199, 0.75)';
    
    const shadowColor = mode === 'dark' 
      ? '0 0 20px rgba(41, 181, 232, 0.5)' 
      : '0 0 18px rgba(41, 181, 232, 0.6)';

    switch (shape.type) {
      case 'cube':
        return (
          <div
            key={shape.id}
            style={baseStyle}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
            }}>
              {/* Caras del cubo */}
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                border: `1px solid ${primaryColor}`,
                transform: `rotateY(0deg) translateZ(${shape.size/2}px)`,
                boxShadow: shadowColor,
              }} />
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${secondaryColor}, ${primaryColor})`,
                border: `1px solid ${primaryColor}`,
                transform: `rotateY(90deg) translateZ(${shape.size/2}px)`,
              }} />
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                border: `1px solid ${primaryColor}`,
                transform: `rotateY(180deg) translateZ(${shape.size/2}px)`,
              }} />
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${secondaryColor}, ${primaryColor})`,
                border: `1px solid ${primaryColor}`,
                transform: `rotateY(-90deg) translateZ(${shape.size/2}px)`,
              }} />
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${primaryColor}, transparent)`,
                border: `1px solid ${primaryColor}`,
                transform: `rotateX(90deg) translateZ(${shape.size/2}px)`,
              }} />
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${secondaryColor}, transparent)`,
                border: `1px solid ${primaryColor}`,
                transform: `rotateX(-90deg) translateZ(${shape.size/2}px)`,
              }} />
            </div>
          </div>
        );

      case 'pyramid':
        return (
          <div
            key={shape.id}
            style={baseStyle}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
            }}>
              {/* Caras de la pirámide */}
              <div style={{
                position: 'absolute',
                width: 0,
                height: 0,
                left: '50%',
                top: '50%',
                borderLeft: `${shape.size/2}px solid transparent`,
                borderRight: `${shape.size/2}px solid transparent`,
                borderBottom: `${shape.size}px solid ${primaryColor}`,
                transform: `translate(-50%, -50%) rotateX(0deg)`,
                boxShadow: shadowColor,
              }} />
              <div style={{
                position: 'absolute',
                width: 0,
                height: 0,
                left: '50%',
                top: '50%',
                borderLeft: `${shape.size/2}px solid transparent`,
                borderRight: `${shape.size/2}px solid transparent`,
                borderBottom: `${shape.size}px solid ${secondaryColor}`,
                transform: `translate(-50%, -50%) rotateY(90deg)`,
              }} />
              <div style={{
                position: 'absolute',
                width: 0,
                height: 0,
                left: '50%',
                top: '50%',
                borderLeft: `${shape.size/2}px solid transparent`,
                borderRight: `${shape.size/2}px solid transparent`,
                borderBottom: `${shape.size}px solid ${primaryColor}`,
                transform: `translate(-50%, -50%) rotateY(180deg)`,
              }} />
              <div style={{
                position: 'absolute',
                width: 0,
                height: 0,
                left: '50%',
                top: '50%',
                borderLeft: `${shape.size/2}px solid transparent`,
                borderRight: `${shape.size/2}px solid transparent`,
                borderBottom: `${shape.size}px solid ${secondaryColor}`,
                transform: `translate(-50%, -50%) rotateY(270deg)`,
              }} />
            </div>
          </div>
        );

      case 'octahedron':
        return (
          <div
            key={shape.id}
            style={baseStyle}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
            }}>
              {/* Pirámide superior */}
              <div style={{
                position: 'absolute',
                width: 0,
                height: 0,
                left: '50%',
                top: '25%',
                borderLeft: `${shape.size/2}px solid transparent`,
                borderRight: `${shape.size/2}px solid transparent`,
                borderBottom: `${shape.size/2}px solid ${primaryColor}`,
                transform: `translate(-50%, -50%)`,
                boxShadow: shadowColor,
              }} />
              {/* Pirámide inferior */}
              <div style={{
                position: 'absolute',
                width: 0,
                height: 0,
                left: '50%',
                top: '75%',
                borderLeft: `${shape.size/2}px solid transparent`,
                borderRight: `${shape.size/2}px solid transparent`,
                borderTop: `${shape.size/2}px solid ${secondaryColor}`,
                transform: `translate(-50%, -50%)`,
              }} />
            </div>
          </div>
        );

      case 'prism':
        return (
          <div
            key={shape.id}
            style={baseStyle}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
            }}>
              {/* Capas del prisma */}
              {[0, 1, 2].map(layer => (
                <div
                  key={layer}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '20%',
                    top: `${layer * 30}%`,
                    background: `linear-gradient(90deg, ${layer % 2 ? primaryColor : secondaryColor}, transparent)`,
                    border: `1px solid ${primaryColor}`,
                    transform: `translateZ(${(layer - 1) * 10}px)`,
                    boxShadow: layer === 0 ? shadowColor : 'none',
                  }}
                />
              ))}
            </div>
          </div>
        );

      case 'diamond':
        return (
          <div
            key={shape.id}
            style={baseStyle}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
            }}>
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `conic-gradient(${primaryColor}, ${secondaryColor}, ${primaryColor})`,
                clipPath: 'polygon(50% 0%, 0% 50%, 50% 100%, 100% 50%)',
                border: `2px solid ${primaryColor}`,
                boxShadow: shadowColor,
                transform: 'rotateZ(45deg)',
              }} />
            </div>
          </div>
        );

      case 'sphere':
        return (
          <div
            key={shape.id}
            style={{
              ...baseStyle,
              borderRadius: '50%',
              background: `radial-gradient(circle at 30% 30%, ${primaryColor}, ${secondaryColor})`,
              border: `2px solid ${primaryColor}`,
              boxShadow: shadowColor,
            }}
          >
            <div style={{
              position: 'absolute',
              top: '20%',
              left: '20%',
              width: '30%',
              height: '30%',
              borderRadius: '50%',
              background: `radial-gradient(circle, rgba(255,255,255,0.8), transparent)`,
              animation: `pulse ${shape.rotationSpeed/4}s ease-in-out infinite`,
            }} />
          </div>
        );

      default:
        return null;
    }
  };

  const getDataFlowLines = () => {
    const lines = [];
    for (let i = 0; i < 6; i++) {
      const delay = i * 3;
      const y = 20 + (i * 15);
      
      lines.push(
        <div
          key={`line-${i}`}
          style={{
            position: 'absolute',
            left: '-10%',
            top: `${y}%`,
            width: '120%',
            height: '1px',
            background: mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(41, 181, 232, 0.7), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(41, 181, 232, 0.65), transparent)',
            animation: `dataFlow ${8 + delay}s ease-in-out infinite`,
            animationDelay: `${delay}s`,
          }}
        />
      );
    }
    return lines;
  };

  const getNetworkNodes = () => {
    const nodes = [];
    for (let i = 0; i < 8; i++) {
      const x = 15 + (i * 12);
      const y = 20 + Math.sin(i) * 30;
      
      nodes.push(
        <div
          key={`node-${i}`}
          style={{
            position: 'absolute',
            left: `${x}%`,
            top: `${y + 40}%`,
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: mode === 'dark' ? '#29B5E8' : '#1B96C7',
            animation: `pulse ${2 + (i * 0.3)}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
            boxShadow: mode === 'dark' 
              ? '0 0 10px rgba(41, 181, 232, 0.6)'
              : '0 0 12px rgba(41, 181, 232, 0.7)',
          }}
        />
      );

      // Conexiones entre nodos
      if (i < 7) {
        const nextX = 15 + ((i + 1) * 12);
        const nextY = 20 + Math.sin(i + 1) * 30;
        const angle = Math.atan2(nextY - y, nextX - x) * 180 / Math.PI;
        const distance = Math.sqrt((nextX - x) ** 2 + (nextY - nextY) ** 2) * 0.8;

        nodes.push(
          <div
            key={`connection-${i}`}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y + 40}%`,
              width: `${distance}%`,
              height: '1px',
              background: mode === 'dark'
                ? 'linear-gradient(90deg, rgba(41, 181, 232, 0.6), rgba(41, 181, 232, 0.3))'
                : 'linear-gradient(90deg, rgba(41, 181, 232, 0.55), rgba(41, 181, 232, 0.3))',
              transformOrigin: 'left center',
              transform: `rotate(${angle}deg)`,
              animation: `connectionPulse ${3 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.7}s`,
            }}
          />
        );
      }
    }
    return nodes;
  };

  return (
    <>
      <style>
        {`
          @keyframes float3D1 {
            0%, 100% { 
              transform: translateY(0px) translateX(0px) translateZ(0px) rotateX(0deg) rotateY(0deg); 
            }
            25% { 
              transform: translateY(-30px) translateX(15px) translateZ(20px) rotateX(90deg) rotateY(45deg); 
            }
            50% { 
              transform: translateY(-15px) translateX(-15px) translateZ(-10px) rotateX(180deg) rotateY(90deg); 
            }
            75% { 
              transform: translateY(-45px) translateX(8px) translateZ(15px) rotateX(270deg) rotateY(135deg); 
            }
          }

          @keyframes float3D-1 {
            0%, 100% { 
              transform: translateY(0px) translateX(0px) translateZ(0px) rotateX(0deg) rotateY(0deg); 
            }
            25% { 
              transform: translateY(30px) translateX(-15px) translateZ(-20px) rotateX(-90deg) rotateY(-45deg); 
            }
            50% { 
              transform: translateY(15px) translateX(15px) translateZ(10px) rotateX(-180deg) rotateY(-90deg); 
            }
            75% { 
              transform: translateY(45px) translateX(-8px) translateZ(-15px) rotateX(-270deg) rotateY(-135deg); 
            }
          }

          @keyframes rotate3D {
            0% { 
              transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg); 
            }
            33% { 
              transform: rotateX(120deg) rotateY(120deg) rotateZ(120deg); 
            }
            66% { 
              transform: rotateX(240deg) rotateY(240deg) rotateZ(240deg); 
            }
            100% { 
              transform: rotateX(360deg) rotateY(360deg) rotateZ(360deg); 
            }
          }

          @keyframes dataFlow {
            0% { 
              transform: translateX(-100%) scaleX(0);
              opacity: 0;
            }
            20% {
              transform: translateX(-50%) scaleX(0.5);
              opacity: 1;
            }
            50% {
              transform: translateX(0%) scaleX(1);
              opacity: 1;
            }
            80% {
              transform: translateX(50%) scaleX(0.5);
              opacity: 1;
            }
            100% { 
              transform: translateX(100%) scaleX(0);
              opacity: 0;
            }
          }

          @keyframes pulse {
            0%, 100% { 
              transform: scale(1);
              opacity: 0.7;
            }
            50% { 
              transform: scale(1.5);
              opacity: 1;
            }
          }

          @keyframes connectionPulse {
            0%, 100% { 
              opacity: 0.1;
              transform: scaleX(0.8);
            }
            50% { 
              opacity: 0.5;
              transform: scaleX(1.2);
            }
          }

          @keyframes matrixRain {
            0% {
              transform: translateY(-100vh);
              opacity: 0;
            }
            10% {
              opacity: 1;
            }
            90% {
              opacity: 1;
            }
            100% {
              transform: translateY(100vh);
              opacity: 0;
            }
          }
        `}
      </style>
      
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {/* Formas geométricas flotantes */}
        {variant === 'geometric' && shapes.map(shape => getShapeElement(shape))}
        
        {/* Líneas de flujo de datos */}
        {variant === 'dataflow' && getDataFlowLines()}
        
        {/* Red de nodos conectados */}
        {variant === 'network' && getNetworkNodes()}
        
        {/* Patrón combinado para login */}
        {variant === 'login' && (
          <>
            {shapes.slice(0, 6).map(shape => getShapeElement(shape))}
            {getDataFlowLines().slice(0, 3)}
            {getNetworkNodes().slice(0, 4)}
          </>
        )}

        {/* Gradiente radial sutil para profundidad */}
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '20%',
            right: '20%',
            bottom: '20%',
            background: mode === 'dark'
              ? 'radial-gradient(ellipse at center, rgba(41, 181, 232, 0.08) 0%, transparent 70%)'
              : 'radial-gradient(ellipse at center, rgba(41, 181, 232, 0.12) 0%, transparent 70%)',
            animation: 'pulse 8s ease-in-out infinite',
          }}
        />
      </div>
    </>
  );
};

export default AnimatedBackground;
